// Match Play runner.
//
// Head-to-head match between two sides. Three valid shapes:
//   • 1-v-1  — two players.
//   • 2-v-2  — four players with Team on: two pairs (better-ball/aggregate).
//   • Dual   — four players with Individual matches on: the fourball splits
//              into TWO independent 1-v-1 matches (e.g. 1-v-2 and 3-v-4), each
//              settled and tracked on its own.
// Each hole, the side with the lower score (gross or net) wins the hole and
// goes one up; a tie halves it. The side ahead at the end of a segment wins
// that segment's bet per player. Segments:
//   • Non-Nassau → one match over all 18 (the "Bet per Player").
//   • Nassau → three matches: front 9, back 9, and overall 18, each its own bet.
//
// The overall-18 match also drives the leaderboard's Score column (e.g. "1 Up")
// and Thru count. A halved segment (all square at the end) pays nothing. In
// Dual mode each of the two matches carries its own Score/Thru/settlement.
//
// Only a 2-player, a 4-player-Team, or a 4-player-Dual round forms a valid
// match; any other shape returns active:false. Pure function — no React,
// no state.

import { Course, Hole, Player, ScoreMap } from "../types";
import { HandicapMode, adjustHandicaps } from "../handicapAdjust";

type TeamSetup = "1-2v3-4" | "1-3v2-4" | "1-4v2-3";
// Pairing for Dual (two 1-v-1 matches) mode. Each value names the two
// head-to-head matches: e.g. "1v2-3v4" → match 1 is P1-v-P2, match 2 is P3-v-P4.
type DualSetup = "1v2-3v4" | "1v3-2v4" | "1v4-2v3";
type TeamFormat = "aggregate" | "betterBall";
export type MatchScoringMode = "net" | "gross";

function courseHandicap(
  handicapIndex: number | undefined,
  slope: number | undefined,
  rating: number | undefined,
  totalPar: number,
): number {
  if (handicapIndex == null) return 0;
  const slopeFactor = slope ? slope / 113 : 1;
  const ratingDiff = rating != null ? rating - totalPar : 0;
  return Math.round(handicapIndex * slopeFactor + ratingDiff);
}

function strokesOnHole(courseHcp: number, hole: Hole): number {
  if (courseHcp <= 0) return 0;
  const base = Math.floor(courseHcp / 18);
  const remainder = courseHcp % 18;
  return base + (hole.si <= remainder ? 1 : 0);
}

function teamsFromSetup(players: Player[], setup?: TeamSetup): [Player[], Player[]] {
  const [a, b, c, d] = players;
  if (setup === "1-3v2-4") return [[a, c], [b, d]];
  if (setup === "1-4v2-3") return [[a, d], [b, c]];
  return [[a, b], [c, d]];
}

// Two head-to-head pairings for Dual mode. Returns [[matchA opponents],
// [matchB opponents]] — each inner pair is the two players facing each other
// in that match. The partition mirrors teamsFromSetup, but the two players in
// a pairing are OPPONENTS here, not partners.
function pairsFromDualSetup(
  players: Player[],
  setup?: DualSetup,
): [[Player, Player], [Player, Player]] {
  const [a, b, c, d] = players;
  if (setup === "1v3-2v4") return [[a, c], [b, d]];
  if (setup === "1v4-2v3") return [[a, d], [b, c]];
  return [[a, b], [c, d]];
}

export interface MatchPlayOptions {
  overallBet?: number;
  nassau?: boolean;
  frontNineBet?: number;
  backNineBet?: number;
  team?: boolean;
  teamSetup?: TeamSetup;
  teamFormat?: TeamFormat;
  // Dual mode — split a fourball into two independent 1-v-1 matches. Mutually
  // exclusive with Team; `dualMatchSetup` picks the pairing.
  dualMatch?: boolean;
  dualMatchSetup?: DualSetup;
  scoringMode?: MatchScoringMode;
  handicapMode?: HandicapMode;
}

export interface MatchPlayPlayer {
  overall: number;
  front: number;
  back: number;
  total: number;
  // Overall-18 match differential from THIS player's side (+ = up, − = down).
  statusDiff: number;
  // Holes of the overall match decided so far (both sides scored).
  thru: number;
  // 0 = first side, 1 = second side, null = 1-v-1 (no team colours).
  team: 0 | 1 | null;
  // True for the first listed player on each side — the only row that shows
  // the up/down Score in team mode.
  firstOnSide: boolean;
  // Which match this player belongs to. Always 0 for 1-v-1 and Team rounds;
  // 0 or 1 in Dual mode (the two separate matches).
  matchIndex: number;
}

export interface MatchPlayResult {
  payouts: Record<string, MatchPlayPlayer>;
  hasAnyBet: boolean;
  nassauActive: boolean;
  teamMode: boolean;
  // Dual (two 1-v-1 matches) round. When true, `matches` lists the pairings.
  dualMode: boolean;
  // Player-id groupings per match, each ordered [sideA, sideB]. Populated for
  // every valid shape (one entry for 1-v-1 / Team, two for Dual) so the board
  // can render one standings panel per match.
  matches: { index: number; playerIds: string[] }[];
  active: boolean;
}

export interface MatchPlayInput {
  course: Course;
  players: Player[];
  scores: ScoreMap;
  options: MatchPlayOptions;
}

export function runMatchPlay(input: MatchPlayInput): MatchPlayResult {
  const { course, players, scores, options } = input;
  const overallBet = Number(options.overallBet) || 0;
  const frontBet = options.nassau ? Number(options.frontNineBet) || 0 : 0;
  const backBet = options.nassau ? Number(options.backNineBet) || 0 : 0;
  const scoringMode: MatchScoringMode =
    options.scoringMode === "net" ? "net" : "gross";
  const teamFormat: TeamFormat =
    options.teamFormat === "aggregate" ? "aggregate" : "betterBall";

  const payouts: Record<string, MatchPlayPlayer> = {};
  for (const p of players) {
    payouts[p.id] = {
      overall: 0,
      front: 0,
      back: 0,
      total: 0,
      statusDiff: 0,
      thru: 0,
      team: null,
      firstOnSide: false,
      matchIndex: 0,
    };
  }

  // Resolve the match(es). Dual takes precedence over Team when both linger in
  // the options bag (the UI keeps them mutually exclusive, but be defensive).
  const dualMode = !!options.dualMatch && players.length === 4;
  const teamMode = !dualMode && !!options.team && players.length === 4;

  interface Match {
    index: number;
    sideA: Player[];
    sideB: Player[];
  }
  const matches: Match[] = [];
  if (dualMode) {
    const [pairA, pairB] = pairsFromDualSetup(players, options.dualMatchSetup);
    matches.push({ index: 0, sideA: [pairA[0]], sideB: [pairA[1]] });
    matches.push({ index: 1, sideA: [pairB[0]], sideB: [pairB[1]] });
  } else if (teamMode) {
    const [teamA, teamB] = teamsFromSetup(players, options.teamSetup);
    matches.push({ index: 0, sideA: teamA, sideB: teamB });
  } else if (players.length === 2) {
    matches.push({ index: 0, sideA: [players[0]], sideB: [players[1]] });
  } else {
    return {
      payouts,
      hasAnyBet: false,
      nassauActive: !!options.nassau,
      teamMode,
      dualMode,
      matches: [],
      active: false,
    };
  }

  // Tag team colour + first-on-side + match index for the board.
  for (const m of matches) {
    m.sideA.forEach((p, i) => {
      payouts[p.id].team = teamMode ? 0 : null;
      payouts[p.id].firstOnSide = i === 0;
      payouts[p.id].matchIndex = m.index;
    });
    m.sideB.forEach((p, i) => {
      payouts[p.id].team = teamMode ? 1 : null;
      payouts[p.id].firstOnSide = i === 0;
      payouts[p.id].matchIndex = m.index;
    });
  }

  // Net-scoring setup.
  const adjusted =
    scoringMode === "net"
      ? adjustHandicaps(players, options.handicapMode)
      : players;
  const totalPar = course.holes.reduce((s, h) => s + h.par, 0);
  const tees = course.tees || [];
  const courseHcpById: Record<string, number> = {};
  for (const p of adjusted) {
    const tee = tees.find((t) => t.id === p.tee);
    courseHcpById[p.id] = courseHandicap(
      p.handicap,
      tee?.slope,
      tee?.rating,
      totalPar,
    );
  }
  const scoreFor = (playerId: string, hole: Hole): number | null => {
    const sc = (scores[playerId] || {})[hole.number];
    if (!sc || typeof sc.gross !== "number") return null;
    if (scoringMode === "gross") return sc.gross;
    return sc.gross - strokesOnHole(courseHcpById[playerId], hole);
  };

  // A side's score on a hole: the player's score (1-v-1), or better-ball /
  // aggregate of whoever on the side has a score (2-v-2). null when no one on
  // the side has scored the hole.
  const sideScore = (side: Player[], hole: Hole): number | null => {
    const vals: number[] = [];
    for (const p of side) {
      const s = scoreFor(p.id, hole);
      if (s != null) vals.push(s);
    }
    if (vals.length === 0) return null;
    return teamFormat === "aggregate"
      ? vals.reduce((a, b) => a + b, 0)
      : Math.min(...vals);
  };

  // Walk a segment hole-by-hole; return side A's net holes (won − lost) and the
  // count of decided holes (both sides scored).
  const matchOver = (
    sideA: Player[],
    sideB: Player[],
    lo: number,
    hi: number,
  ): { diffA: number; thru: number } => {
    const holes = course.holes
      .filter((h) => h.number >= lo && h.number <= hi)
      .sort((a, b) => a.number - b.number);
    let aWon = 0;
    let bWon = 0;
    let thru = 0;
    for (const hole of holes) {
      const a = sideScore(sideA, hole);
      const b = sideScore(sideB, hole);
      if (a == null || b == null) continue;
      thru += 1;
      if (a < b) aWon += 1;
      else if (b < a) bWon += 1;
      // tie → halved
    }
    return { diffA: aWon - bWon, thru };
  };

  const settleSegment = (
    sideA: Player[],
    sideB: Player[],
    lo: number,
    hi: number,
    bet: number,
    bucket: "overall" | "front" | "back",
  ) => {
    if (bet <= 0) return;
    const { diffA } = matchOver(sideA, sideB, lo, hi);
    if (diffA === 0) return; // all square → no money
    const aWins = diffA > 0;
    for (const p of sideA) payouts[p.id][bucket] += aWins ? bet : -bet;
    for (const p of sideB) payouts[p.id][bucket] += aWins ? -bet : bet;
  };

  // Settle each match independently. The overall-18 sub-match drives each
  // player's Score / Thru as well as the overall bet.
  for (const m of matches) {
    const overall = matchOver(m.sideA, m.sideB, 1, 18);
    for (const p of m.sideA) {
      payouts[p.id].statusDiff = overall.diffA;
      payouts[p.id].thru = overall.thru;
    }
    for (const p of m.sideB) {
      payouts[p.id].statusDiff = -overall.diffA;
      payouts[p.id].thru = overall.thru;
    }

    settleSegment(m.sideA, m.sideB, 1, 18, overallBet, "overall");
    if (options.nassau) {
      settleSegment(m.sideA, m.sideB, 1, 9, frontBet, "front");
      settleSegment(m.sideA, m.sideB, 10, 18, backBet, "back");
    }
  }

  for (const p of players) {
    const pay = payouts[p.id];
    pay.total = pay.overall + pay.front + pay.back;
  }

  const hasAnyBet =
    overallBet > 0 || (!!options.nassau && (frontBet > 0 || backBet > 0));

  return {
    payouts,
    hasAnyBet,
    nassauActive: !!options.nassau,
    teamMode,
    dualMode,
    matches: matches.map((m) => ({
      index: m.index,
      playerIds: [...m.sideA, ...m.sideB].map((p) => p.id),
    })),
    active: true,
  };
}
